import { useCallback } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import {
  SubscriptionGeneralTabDocument,
  SubscriptionPaymentHistoryTabDocument,
  useManageSeatCountModalQuery,
  useUpdateSubscriptionMutation
} from 'generated/graphql';
import _ from 'lodash';
import { appFormUtil } from 'modules/common';
import { StyledInputTextField } from 'modules/components/layer01';



export type ManageSeatCountModalProps = {
  organizationId: string;

  onClose: () => void;
};

export const FC = (props: ManageSeatCountModalProps) => {
  const { data, loading } = useManageSeatCountModalQuery({
    variables: {
      organizationId: props.organizationId
    }
  });
  const [
    updateSubscription,
    updateSubscriptionTuple
  ] = useUpdateSubscriptionMutation({
    refetchQueries: [
      {
        query: SubscriptionGeneralTabDocument,
        variables: {
          organizationId: props.organizationId
        }
      },
      {
        query: SubscriptionPaymentHistoryTabDocument,
        variables: {
          organizationId: props.organizationId
        }
      }
    ]
  });

  const {
    formState,
    genFieldProps,
    dispatch
  } = appFormUtil.useFormState<{
    seatCount: string;
    formStep: 'edit' | 'confirm';
  }>({
    initialState: {
      seatCount: (data?.subscriptionPlan?.seatCount ?? 0).toString(),
      formStep: 'edit'
    },

    key2FieldConfig: {
      seatCount: {
        fnValidate: (value) => {
          const num = +value;

          if (num < (data?.organization?.memberships.length ?? 1)) {
            return 'Must have enough seats to cover all users.';
          }

          if (num > 40) {
            return 'Cannot have more than 40 seats';
          }
          return '';
        }
      }
    }
  });



  const handleSubmit = useCallback(async () => {
    const newCount = +formState.seatCount;
    if (_.isNaN(newCount)) {
      window.alert('Invalid seat count');
      return;
    }

    if (newCount < (data?.organization?.memberships.length ?? 0)) {
      window.alert('Must have enough seats to cover your existing members.');
      return;
    }

    if (newCount === (data?.subscriptionPlan?.seatCount ?? 0)) {
      window.alert('You must change the seat count to continue.');
      return;
    }

    if (formState.formStep === 'edit') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'formStep',
          value: 'confirm'
        }
      });
    } else if (formState.formStep === 'confirm') {
      try {
        await updateSubscription({
          variables: {
            organizationId: props.organizationId,
            seatCount: +formState.seatCount
          }
        });
        dispatch({
          type: 'UPDATE',
          payload: {
            property: 'formStep',
            value: 'complete'
          }
        });
        props.onClose();

      } catch (err) {
        if (err instanceof Error) {
          window.alert(err.message);
        }
      }
    }
  }, [
    data?.organization?.memberships.length,
    data?.subscriptionPlan?.seatCount,
    dispatch,
    formState.formStep,
    formState.seatCount,
    props,
    updateSubscription
  ]);



  const handleClickCancel = useCallback(() => {
    if (formState.formStep === 'edit') {
      props.onClose();
    } else if (formState.formStep === 'confirm') {
      dispatch({
        type: 'UPDATE',
        payload: {
          property: 'formStep',
          value: 'edit'
        }
      });
    }

  }, [
    dispatch,
    formState.formStep,
    props
  ]);



  const isLoading = loading || updateSubscriptionTuple.loading;

  return (
    <Dialog
      open
      onClose={props.onClose}
    >
      <DialogTitle>
        Manage Seat Count
      </DialogTitle>

      <DialogContent>
        {formState.formStep === 'edit' && (
          <Stack gap={1}>
            <Typography variant='formLabel'>
              Available seats
            </Typography>

            <Stack
              direction='row'
              gap={2}
              divider={<Divider orientation='vertical' />}
            >
              <Typography>Total: {data ? data.subscriptionPlan?.seatCount : <Skeleton />}</Typography>

              <Typography>Active: {data ? data.organization?.memberships.length : <Skeleton />}</Typography>

              {data ? (
                <Typography>
                  Free: {(data.subscriptionPlan?.seatCount ?? 0) - (data.organization?.memberships.length ?? 0)}
                </Typography>
              ) : (
                <Typography>
                  <Skeleton />
                </Typography>
              )}
            </Stack>

            <Divider />

            <StyledInputTextField.FC
              type='number'
              label='New seat count'
              {...genFieldProps('seatCount')}
            />
          </Stack>

        )}

        {formState.formStep === 'confirm' && (
          <Typography>
            Your new seat count will be {formState.seatCount}. Are you sure you want to make this change?
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant='outlined'
          color='secondary'
          onClick={handleClickCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>

        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={isLoading}
        >
          {formState.formStep === 'edit' ? 'Submit' : 'Purchase seats'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
